import { useState, useEffect, useMemo } from 'react'
import {
    ScanEye,
    Sparkles,
    DollarSign,
    Activity,
    Target,
    Repeat,
    CreditCard,
} from 'lucide-react'
import { DashboardSkeleton } from '@/components/skeletons'
import { RelativeTime } from '@/components/RelativeTime'
import { XAxis, YAxis, AreaChart, Area } from 'recharts'
import { Link } from 'react-router-dom'

// shadcn/ui components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    ChartConfig,
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent,
} from '@/components/ui/chart'

import { DashboardDateRangePicker } from '@/components/ui/dashboard-date-range-picker'
import {
    xrayAdminService,
    mapXrayChartRows,
    type AdminXrayOverviewStats,
    type AdminXrayChartPoint,
    type AdminXrayRecentSub,
    type AdminXrayRecentTopup,
    type AdminXrayTopAgency,
    type XrayChartMetric,
} from '@/services/xrayAdmin'
import { useAuth } from '@/contexts/AuthContext'
import { formatDollarsFromCents } from '@/lib/xray-pricing'

// =========================================================================
// Chart options
// =========================================================================

interface ChartOption {
    key: XrayChartMetric
    label: string
    prefix?: string
}

const chartOptions: ChartOption[] = [
    { key: 'lookups', label: 'Total Lookups' },
    { key: 'billableLookups', label: 'Billable Lookups' },
    { key: 'topupRevenue', label: 'Top-up Revenue', prefix: '$' },
    { key: 'subMrrAdded', label: 'New Subscription Revenue', prefix: '$' },
]

const chartConfig = {
    value: {
        label: 'Value',
        color: '#6366f1',
    },
} satisfies ChartConfig

// =========================================================================
// Stat cards
// =========================================================================

const formatPercent = (value: number) => `${(value * 100).toFixed(1)}%`

const getStatsCards = (stats: AdminXrayOverviewStats | null) => [
    {
        title: 'Active Subscriptions',
        value: stats?.activeSubs?.toLocaleString() ?? '-',
        change: '',
        icon: ScanEye,
        description: 'All X-Ray subscriptions currently active',
        iconBg: 'bg-indigo-100 dark:bg-indigo-950/50',
        iconColor: 'text-indigo-600 dark:text-indigo-400',
    },
    {
        title: 'Paid Subscriptions',
        value: stats?.paidSubs?.toLocaleString() ?? '-',
        change: '',
        icon: Sparkles,
        description: 'Active paid X-Ray subscriptions',
        iconBg: 'bg-emerald-100 dark:bg-emerald-950/50',
        iconColor: 'text-emerald-600 dark:text-emerald-400',
    },
    {
        title: 'X-Ray MRR',
        value: stats?.mrr != null ? `$${stats.mrr.toLocaleString()}` : '-',
        change: '',
        icon: DollarSign,
        description: 'Sum of monthly prices across paid subs',
        iconBg: 'bg-violet-100 dark:bg-violet-950/50',
        iconColor: 'text-violet-600 dark:text-violet-400',
    },
    {
        title: 'Top-up Revenue',
        value: stats != null ? `$${(stats.topupRevenueCents / 100).toLocaleString()}` : '-',
        change: stats?.periodComparison?.topupRevenueChange ?? '',
        icon: CreditCard,
        description: 'Wallet top-ups and auto-recharges in period',
        iconBg: 'bg-pink-100 dark:bg-pink-950/50',
        iconColor: 'text-pink-600 dark:text-pink-400',
    },
    {
        title: 'Total Lookups',
        value: stats?.totalLookups?.toLocaleString() ?? '-',
        change: stats?.periodComparison?.totalLookupsChange ?? '',
        icon: Activity,
        description: 'Every lookup recorded in period',
        iconBg: 'bg-blue-100 dark:bg-blue-950/50',
        iconColor: 'text-blue-600 dark:text-blue-400',
    },
    {
        title: 'Check Success Rate',
        value: stats != null ? formatPercent(stats.matchRate) : '-',
        change: stats?.periodComparison?.matchRateChange ?? '',
        icon: Target,
        description: 'Charged ÷ (charged + failed) credit checks',
        iconBg: 'bg-cyan-100 dark:bg-cyan-950/50',
        iconColor: 'text-cyan-600 dark:text-cyan-400',
    },
    {
        title: 'Auto-recharges',
        value: stats?.autoRechargeCount?.toLocaleString() ?? '-',
        change: stats?.periodComparison?.autoRechargeCountChange ?? '',
        icon: Repeat,
        description: 'Automatic wallet recharges in period',
        iconBg: 'bg-orange-100 dark:bg-orange-950/50',
        iconColor: 'text-orange-600 dark:text-orange-400',
    },
]

// =========================================================================
// Page
// =========================================================================

const XrayOverview = () => {
    const { user } = useAuth() as any
    const [selectedChart, setSelectedChart] = useState<XrayChartMetric>('lookups')
    const [dateRange, setDateRange] = useState<string>('1m')

    const [isLoading, setIsLoading] = useState(true)
    const [stats, setStats] = useState<AdminXrayOverviewStats | null>(null)
    const [chartRows, setChartRows] = useState<AdminXrayChartPoint[]>([])
    const [recentSubs, setRecentSubs] = useState<AdminXrayRecentSub[]>([])
    const [recentPurchases, setRecentPurchases] = useState<AdminXrayRecentTopup[]>([])
    const [topAgencies, setTopAgencies] = useState<AdminXrayTopAgency[]>([])

    useEffect(() => {
        const loadOverview = async () => {
            setIsLoading(true)
            try {
                const [s, chart, subs, purchases, top] = await Promise.all([
                    xrayAdminService.getOverviewStats(dateRange),
                    xrayAdminService.getOverviewChartRows(dateRange),
                    xrayAdminService.getRecentSubs(),
                    xrayAdminService.getRecentTopups(),
                    xrayAdminService.getTopAgencies(dateRange),
                ])

                setStats(s)
                setChartRows(chart)
                setRecentSubs(subs)
                setRecentPurchases(purchases)
                setTopAgencies(top)
            } catch (error) {
                console.error('Failed to load X-Ray overview:', error)
            } finally {
                setIsLoading(false)
            }
        }

        loadOverview()
    }, [dateRange])

    // The chart payload carries every metric for every day, so switching the
    // metric (or the viewer's time zone) re-maps rows already in hand. The rows
    // are fetched once per date range, in loadOverview above.
    const chartData = useMemo(
        () => mapXrayChartRows(chartRows, selectedChart, user?.timezone),
        [chartRows, selectedChart, user?.timezone]
    )

    const currentChartOption = chartOptions.find((opt) => opt.key === selectedChart) || chartOptions[0]
    const statsCards = getStatsCards(stats)

    if (isLoading) {
        return <DashboardSkeleton />
    }

    return (
        <div className="space-y-4 sm:space-y-6 w-full px-4 sm:px-0 animate-in fade-in duration-300">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">
                    X-Ray Overview
                </h1>

                <DashboardDateRangePicker
                    value={dateRange}
                    onChange={setDateRange}
                    tooltipMessage="Filters period-bounded metrics (top-up revenue, lookups, check success rate, auto-recharges) and trend chart. Snapshot metrics like active subs and MRR always reflect current state."
                />
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 xs:grid-cols-2 xl:grid-cols-4 gap-4">
                {statsCards.map((stat, index) => (
                    <Card
                        key={stat.title}
                        className="animate-in fade-in duration-300 py-0"
                        style={{ animationDelay: `${index * 50}ms` }}
                    >
                        <CardContent className="p-4">
                            <div className="flex items-center gap-4">
                                <div className={`flex h-12 w-12 items-center justify-center rounded-lg shrink-0 ${stat.iconBg} ${stat.iconColor}`}>
                                    <stat.icon className="h-6 w-6" />
                                </div>
                                <div className="flex flex-col min-w-0">
                                    <span className="text-2xl font-bold text-foreground leading-tight">
                                        {stat.value}
                                    </span>
                                    <span className="text-sm text-muted-foreground truncate">
                                        {stat.title}
                                    </span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Chart */}
            <Card className="py-0 gap-0">
                <CardHeader className="py-6 gap-0 border-b">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <CardTitle className="text-lg font-semibold">
                            {currentChartOption.label} Over Time
                        </CardTitle>
                        <div className="flex items-center gap-2">
                            <Select value={selectedChart} onValueChange={(v) => setSelectedChart(v as XrayChartMetric)}>
                                <SelectTrigger className="w-full sm:w-[300px]">
                                    <div className="flex items-center gap-1 min-w-0">
                                        <span className="text-muted-foreground shrink-0">Metric:</span>
                                        <SelectValue placeholder="Select" />
                                    </div>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectGroup>
                                        {chartOptions.map((option) => (
                                            <SelectItem key={option.key} value={option.key}>
                                                {option.label}
                                            </SelectItem>
                                        ))}
                                    </SelectGroup>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <CardDescription>
                        Daily trend for the selected metric
                    </CardDescription>
                </CardHeader>
                <CardContent className="pt-6 pb-6">
                    {chartData.length > 0 ? (
                        <ChartContainer config={chartConfig} className="h-[280px] w-full">
                            <AreaChart
                                data={chartData}
                                accessibilityLayer
                                margin={{ left: 0, right: 12, top: 12, bottom: 0 }}
                            >
                                <defs>
                                    <linearGradient id="xrayChartGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="var(--color-value)" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="var(--color-value)" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tickMargin={8} />
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    tickMargin={8}
                                    width={40}
                                    tickFormatter={(value) => currentChartOption.prefix ? `${currentChartOption.prefix}${value}` : value}
                                />
                                <ChartTooltip
                                    content={
                                        <ChartTooltipContent
                                            labelFormatter={(_label, payload) => {
                                                return payload?.[0]?.payload?.fullDate || _label
                                            }}
                                            formatter={(value, _name, item: any) => (
                                                <>
                                                    <div
                                                        className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
                                                        style={{ backgroundColor: item?.color || item?.payload?.fill || 'var(--color-value)' }}
                                                    />
                                                    <div className="flex flex-1 justify-between leading-none items-center gap-4">
                                                        <span className="text-muted-foreground">
                                                            {currentChartOption.label}
                                                        </span>
                                                        <span className="text-foreground font-mono font-medium tabular-nums">
                                                            {currentChartOption.prefix ? `${currentChartOption.prefix}${Number(value).toLocaleString()}` : Number(value).toLocaleString()}
                                                        </span>
                                                    </div>
                                                </>
                                            )}
                                        />
                                    }
                                />
                                <Area
                                    type="monotone"
                                    dataKey="value"
                                    stroke="var(--color-value)"
                                    strokeWidth={2}
                                    fill="url(#xrayChartGradient)"
                                />
                            </AreaChart>
                        </ChartContainer>
                    ) : (
                        <div className="h-[280px] flex items-center justify-center text-muted-foreground">
                            No data available for this period
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Bottom: three lists */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Recent X-Ray Signups */}
                <Card className="py-0 gap-0">
                    <CardHeader className="py-6 gap-0 border-b">
                        <CardTitle className="text-lg font-semibold">Recent X-Ray Signups</CardTitle>
                        <CardDescription>Latest X-Ray subscriptions</CardDescription>
                    </CardHeader>
                    <CardContent className="pt-6 pb-6">
                        {recentSubs.length > 0 ? (
                            <div className="space-y-2">
                                {recentSubs.map((sub) => (
                                    <Link
                                        key={sub.id}
                                        to={`/agencies/${sub.agencyPublicId}`}
                                        className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-transparent hover:bg-muted transition-colors"
                                    >
                                        <div className="flex flex-col min-w-0">
                                            <span className="font-medium text-sm text-foreground truncate">
                                                {sub.agencyName}
                                            </span>
                                            <span className="text-xs text-muted-foreground flex items-center mt-0.5">
                                                <RelativeTime date={sub.createdAt} className="cursor-default" />
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <Badge variant="secondary" className="font-medium capitalize text-xs shadow-none cursor-default">
                                                {sub.plan}
                                            </Badge>
                                            {sub.status === 'trialing' && (
                                                <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 font-medium text-xs border-transparent shadow-none cursor-default">
                                                    Trial
                                                </Badge>
                                            )}
                                            {sub.cancelAtPeriodEnd && (
                                                <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 font-medium capitalize text-xs border-transparent shadow-none cursor-default">
                                                    Cancelling
                                                </Badge>
                                            )}
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        ) : (
                            <div className="py-12 text-center">
                                <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                                    <Sparkles className="h-6 w-6 text-muted-foreground" />
                                </div>
                                <h3 className="text-lg font-medium mb-2">No signups yet</h3>
                                <p className="text-sm text-muted-foreground">
                                    No agency has subscribed to X-Ray.
                                </p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Recent wallet top-ups */}
                <Card className="py-0 gap-0">
                    <CardHeader className="py-6 gap-0 border-b">
                        <CardTitle className="text-lg font-semibold">Recent Wallet Top-ups</CardTitle>
                        <CardDescription>Manual top-ups, auto-recharges and admin gifts</CardDescription>
                    </CardHeader>
                    <CardContent className="pt-6 pb-6">
                        {recentPurchases.length > 0 ? (
                            <div className="space-y-2">
                                {recentPurchases.map((p) => (
                                    <Link
                                        key={p.id}
                                        to={`/agencies/${p.agencyPublicId}`}
                                        className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-transparent hover:bg-muted transition-colors"
                                    >
                                        <div className="flex flex-col min-w-0">
                                            <span className="font-medium text-sm text-foreground truncate">
                                                {p.agencyName}
                                            </span>
                                            <span className="text-xs text-muted-foreground flex items-center mt-0.5">
                                                <RelativeTime date={p.createdAt} className="cursor-default" />
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <span className="text-sm font-semibold text-foreground tabular-nums">
                                                {formatDollarsFromCents(p.amountCents)}
                                            </span>
                                            <Badge
                                                variant="secondary"
                                                className={`font-medium text-xs shadow-none cursor-default capitalize ${
                                                    p.triggerType === 'auto'
                                                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-transparent'
                                                        : p.triggerType === 'admin_gift'
                                                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-transparent'
                                                            : ''
                                                }`}
                                            >
                                                {p.triggerType.replace('_', ' ')}
                                            </Badge>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        ) : (
                            <div className="py-12 text-center">
                                <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                                    <CreditCard className="h-6 w-6 text-muted-foreground" />
                                </div>
                                <h3 className="text-lg font-medium mb-2">No wallet top-ups</h3>
                                <p className="text-sm text-muted-foreground">
                                    No top-ups have been purchased yet.
                                </p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Top Agencies by Usage */}
                <Card className="py-0 gap-0">
                    <CardHeader className="py-6 gap-0 border-b">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-lg font-semibold">Top Agencies</CardTitle>
                            <Button variant="outline" size="sm" className="text-xs h-7 px-3" asChild>
                                <Link to="/agencies">View All</Link>
                            </Button>
                        </div>
                        <CardDescription>Ranked by credit checks charged in period</CardDescription>
                    </CardHeader>
                    <CardContent className="pt-6 pb-6">
                        {topAgencies.length > 0 ? (
                            <div className="space-y-2">
                                {topAgencies.map((agency) => (
                                    <Link
                                        key={agency.agencyPublicId ?? agency.agencyName}
                                        to={agency.agencyPublicId ? `/agencies/${agency.agencyPublicId}` : '#'}
                                        className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-transparent hover:bg-muted transition-colors"
                                    >
                                        <div className="flex flex-col min-w-0">
                                            <span className="font-medium text-sm text-foreground truncate">
                                                {agency.agencyName}
                                            </span>
                                            <span className="text-xs text-muted-foreground mt-0.5">{agency.plan}</span>
                                        </div>
                                        <div className="flex flex-col items-end shrink-0">
                                            <span className="font-semibold text-sm text-foreground tabular-nums">
                                                {agency.creditChecks.toLocaleString()} {agency.creditChecks === 1 ? 'check' : 'checks'}
                                            </span>
                                            <span className="text-xs text-muted-foreground mt-0.5 tabular-nums">
                                                {formatDollarsFromCents(agency.spentCents)} spent
                                            </span>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        ) : (
                            <div className="py-12 text-center">
                                <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                                    <ScanEye className="h-6 w-6 text-muted-foreground" />
                                </div>
                                <h3 className="text-lg font-medium mb-2">No credit checks yet</h3>
                                <p className="text-sm text-muted-foreground">
                                    No agency was charged for a credit check in this period.
                                </p>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    )
}

export default XrayOverview
